import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { errMsg, mxn } from "@/lib/api";

export function StampStatus({ status }) {
  const done = status === "delivered";
  return <Badge className={`rounded-full ${done ? "bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]" : "bg-amber-500 hover:bg-amber-500"}`}>{done ? "Entregado" : "Pendiente"}</Badge>;
}

export function useStampPackages() {
  const [pkgs, setPkgs] = useState(null);
  useEffect(() => { api.get("/stamps/packages").then(({ data }) => setPkgs(data.packages)).catch(() => setPkgs([])); }, []);
  return pkgs;
}

export function StampDialog({ open, onOpenChange, licenses, tier, balance, onDone }) {
  const pkgs = useStampPackages();
  const [licId, setLicId] = useState("");
  const [pkgId, setPkgId] = useState("");
  const [payWith, setPayWith] = useState("balance");
  const [busy, setBusy] = useState(false);
  const ready = licenses.filter((l) => l.license_key && l.status === "active");
  const pkg = (pkgs || []).find((p) => p.id === pkgId);
  const discount = pkg ? Math.round(pkg.price * tier.discount) / 100 : 0;
  const total = pkg ? pkg.price - discount : 0;

  const pay = async () => {
    if (!licId) return toast.error("Selecciona la licencia");
    if (!pkg) return toast.error("Selecciona un paquete de timbres");
    if (payWith === "balance" && total > balance) return toast.error("Saldo insuficiente. Paga con Mercado Pago o solicita la licencia como adeudo.");
    setBusy(true);
    try {
      const { data } = await api.post("/distributor/stamps/checkout", { license_id: licId, package_id: pkgId, pay_with: payWith });
      if (data.paid_with_balance) {
        toast.success("Compra registrada. Cargaremos tus timbres en breve.");
        onOpenChange(false);
        setBusy(false);
        onDone();
      } else window.location.assign(data.init_point);
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="stamp-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Comprar timbres fiscales</DialogTitle>
          <DialogDescription>Tu nivel {tier.name} te da {tier.discount}% de descuento en cada paquete.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Licencia</Label>
            <Select value={licId} onValueChange={setLicId}>
              <SelectTrigger data-testid="stamp-license-select"><SelectValue placeholder={ready.length ? "Selecciona la licencia" : "No tienes licencias activadas"} /></SelectTrigger>
              <SelectContent>
                {ready.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`stamp-license-${l.id}`}>{l.business_name || l.customer_name} · {l.license_key}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Paquete</Label>
            {!pkgs ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
              <div className="grid gap-2 sm:grid-cols-3">
                {pkgs.map((p) => (
                  <button key={p.id} type="button" onClick={() => setPkgId(p.id)} data-testid={`stamp-pkg-${p.id}`}
                    className={`rounded-xl border p-3 text-left transition-colors duration-200 ${pkgId === p.id ? "border-primary bg-accent" : "border-border hover:border-foreground/30"}`}>
                    <p className="text-sm font-semibold">{p.name}</p>
                    <p className="text-xs text-muted-foreground line-through">{mxn(p.price)}</p>
                    <p className="font-display text-lg font-bold">{mxn(p.price - Math.round(p.price * tier.discount) / 100)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          {pkg && (
            <div className="space-y-1 rounded-xl border border-border bg-background p-4 text-sm" data-testid="stamp-summary">
              <div className="flex justify-between"><span>{pkg.name}</span><span>{mxn(pkg.price)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Descuento {tier.name} ({tier.discount}%)</span><span>- {mxn(discount)}</span></div>
              <div className="flex justify-between border-t border-border pt-2 font-semibold"><span>Total</span><span data-testid="stamp-total">{mxn(total)}</span></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {[["balance", `Con mi saldo (${mxn(balance)})`], ["mercadopago", "Mercado Pago"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setPayWith(v)} data-testid={`stamp-pay-with-${v}`}
                className={`rounded-xl border p-3 text-sm font-medium transition-colors duration-200 ${payWith === v ? "border-primary bg-accent" : "border-border hover:border-foreground/30"}`}>{l}</button>
            ))}
          </div>
          <Button onClick={pay} disabled={busy} size="lg" className="w-full rounded-full" data-testid="stamp-pay-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Comprar ${pkg ? mxn(total) : ""}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function StampOrdersTable({ items }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      {items.length === 0 ? <p className="py-16 text-center text-muted-foreground">Aún no has comprado timbres.</p> : (
        <Table data-testid="stamp-orders-table">
          <TableHeader><TableRow>
            <TableHead>Fecha</TableHead><TableHead>Paquete</TableHead><TableHead>Licencia</TableHead><TableHead>Total</TableHead><TableHead className="text-right">Estado</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id}>
                <TableCell>{new Date(s.created_at).toLocaleDateString("es-MX")}</TableCell>
                <TableCell>{s.package_name}</TableCell>
                <TableCell><div className="text-sm">{s.business_name}</div><div className="font-mono text-xs text-muted-foreground">{s.activation_id}</div></TableCell>
                <TableCell className="font-semibold">{mxn(s.amount)}</TableCell>
                <TableCell className="text-right"><StampStatus status={s.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
